/* Make a list fill the rest of the screen and scroll inside itself.
 *
 * WHY. A table's header row and the filters above it are the two things you need
 * while you are looking at row ninety. The header was already `position: sticky`, and
 * it never stuck, for a reason that is easy to miss: sticky sticks to the NEAREST
 * scrolling ancestor, and the table sits in a wrapper with overflow-x so it can be
 * panned sideways. That wrapper is the nearest scroller, it never scrolls vertically
 * because it is as tall as its rows, and so the header stuck to nothing while the page
 * scrolled underneath. The filters are above the table and scroll away with the page.
 *
 * THE FIX. Give the wrapper a height that ends where the screen ends. It then really
 * does scroll, so its sticky header works with no further change; and because the
 * table now fits the screen the page itself does not need to scroll, so the filters
 * above it never leave. Frozen columns and sideways panning keep working: it is still
 * one scroller.
 *
 * Measured from where the table starts INSIDE the scrolling page rather than from where
 * it is on screen, so the answer does not change as the page moves, which would make
 * the table grow as you scrolled.
 *
 * NOT ON A PHONE. Under 900px the table is a wide thing to be panned and the page is
 * the thing to scroll, and a nested scroller there fights the browser's own.
 * NEVER SMALLER THAN 320px. On a short window the page scrolls as it did before, which
 * is a worse place to be but a usable one.
 */
import { useLayoutEffect, type RefObject } from 'react';

const MIN_WIDTH = 900;
const MIN_HEIGHT = 320;
/** Left under the table so its last row is not hard against the window edge. */
const BREATHING = 16;

function scrollParentOf(el: HTMLElement): HTMLElement | null {
  let p = el.parentElement;
  while (p) {
    const oy = getComputedStyle(p).overflowY;
    if (oy === 'auto' || oy === 'scroll') return p;
    p = p.parentElement;
  }
  return null;
}

export function useFillScreen(ref: RefObject<HTMLElement | null>, enabled = true): void {
  /* Every render, with no dependency list: what sits above the table (tiles that have
     just loaded, filters that have wrapped onto a second line) changes where it starts,
     and the render that follows a data load is exactly when that happens. Cheap, since
     it reads two rectangles and writes one style. */
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const parent = scrollParentOf(el);

    const fit = (): void => {
      if (!enabled || !parent || window.innerWidth < MIN_WIDTH) {
        el.style.maxHeight = '';
        return;
      }
      const top =
        el.getBoundingClientRect().top - parent.getBoundingClientRect().top + parent.scrollTop;
      const room = parent.clientHeight - top - BREATHING;
      el.style.maxHeight = `${Math.max(MIN_HEIGHT, Math.floor(room))}px`;
    };

    fit();
    if (!parent) return undefined;
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(fit) : null;
    ro?.observe(parent);
    window.addEventListener('resize', fit);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', fit);
    };
  });
}
